import { toast } from "sonner"

import { apiWrapper } from "@/http/api-wrapper"
import { ensureFreshAccessToken } from "@/http/auth/session"
import { isTokenExpired } from "@/http/jwt"
import { env } from "@/lib/env"
import { secureStorage } from "@/lib/secure-storage"

interface ICustomRequestInit extends Omit<RequestInit, "body"> {
  revalidateTags?: string[]
  json?: unknown
  retry?: {
    limit?: number
    methods?: string[]
    statusCodes?: number[]
    backoffLimit?: number
  }
}

const apiURL = env.apiUrl

const defaultRetryConfig = {
  limit: 5,
  methods: ["get"],
  statusCodes: [413],
  backoffLimit: 3000,
}

async function beforeRequestHook(request: Request): Promise<Request> {
  const tokens = secureStorage.getTokens()
  const headers = new Headers(request.headers)

  // Add Authorization header if we have an access token
  if (tokens?.access) {
    headers.set("Authorization", `Bearer ${tokens.access}`)
  }

  // Add security headers
  const hasBody = request.body !== null
  const contentType = headers.get("Content-Type")
  if (hasBody) {
    headers.set("Content-Type", contentType || "application/json")
  } else if (contentType === "application/json") {
    headers.delete("Content-Type")
  }
  headers.set("X-Requested-With", "XMLHttpRequest") // CSRF protection

  // Validate URL to prevent SSRF attacks
  const url = new URL(request.url)
  if (!url.hostname.includes(new URL(apiURL).hostname)) {
    throw new Error("Invalid API endpoint")
  }

  // Ensure cookies are always sent with requests
  // This is important for server-side httpOnly cookie support
  return new Request(request, {
    headers,
    credentials: "include", // Always include cookies
  })
}

async function handleSessionExpired(force = false) {
  const tokens = secureStorage.getTokens()
  const accessExpired = isTokenExpired(tokens.access)
  const refreshExpired = isTokenExpired(tokens.refresh)
  if (!force && (!accessExpired || !refreshExpired)) return

  // Clear state and notify user
  const { clearAllAppState } = await import("@/http/clear-app-state")
  clearAllAppState()
  // Emit toast before navigating for better UX in SPA
  try {
    toast.warning("Sessão expirada. Faça login novamente.")
  } catch {}

  if (typeof window !== "undefined") {
    // Use hard redirect to ensure state reset and route change
    setTimeout(() => {
      window.location.replace("/")
    }, 0)
  }
}
async function afterResponseHook(
  _request: Request,
  response: Response,
  context?: { attemptedRefresh?: boolean },
): Promise<void> {
  const tokens = secureStorage.getTokens()
  const tokensFullyExpired =
    isTokenExpired(tokens.access) && isTokenExpired(tokens.refresh)

  // Show permission error only when the request was not part of a refresh retry
  if (
    response.status === 403 &&
    !context?.attemptedRefresh &&
    !tokensFullyExpired
  ) {
    try {
      toast.error("Acesso negado. Sua sessão continua ativa.")
    } catch {}
  }
}

async function fetchWithRetry(
  input: RequestInfo,
  init?: ICustomRequestInit,
): Promise<Response> {
  const retryConfig = init?.retry || defaultRetryConfig

  const method =
    input instanceof Request
      ? input.method.toLowerCase()
      : (init?.method || "GET").toLowerCase()
  let attempt = 0

  while (true) {
    attempt++
    const response =
      input instanceof Request ? await fetch(input) : await fetch(input, init)

    const shouldRetry =
      retryConfig.statusCodes?.includes(response.status) &&
      retryConfig.methods?.includes(method) &&
      attempt < (retryConfig.limit ?? 5)

    if (!shouldRetry) return response

    const backoff = Math.min(
      2 ** attempt * 100,
      retryConfig.backoffLimit ?? 3000,
    )
    await new Promise((resolve) => setTimeout(resolve, backoff))
  }
}

async function apiClient<T>(
  endpoint: string,
  options: ICustomRequestInit = {},
): Promise<T> {
  const url = endpoint.startsWith("http")
    ? endpoint
    : `${apiURL.replace(/\/$/, "")}/${endpoint.replace(/^\//, "")}`

  let attemptedRefresh = false

  // Apply default options
  const init: ICustomRequestInit = {
    credentials: "include",
    ...options,
  }

  // Create the request promise
  const requestPromise = (async (): Promise<T> => {
    let body: RequestInit["body"] | undefined
    if (options.json) {
      body = JSON.stringify(options.json)
      init.headers = {
        ...init.headers,
        "Content-Type": "application/json",
      }
    }

    // Ensure we have a fresh token when needed
    await ensureFreshAccessToken()

    // Helper to build and send the request
    const send = async (attempted = false) => {
      let req = new Request(url, { ...(init as RequestInit), body })
      req = await beforeRequestHook(req)
      const res = await fetchWithRetry(req, init)
      await afterResponseHook(req, res, { attemptedRefresh: attempted })
      return res
    }

    // First attempt
    let response = await send()

    // If unauthorized, try a single forced refresh and retry once
    if (response.status === 401) {
      const refreshed = await ensureFreshAccessToken(true)
      attemptedRefresh = true

      // If refresh failed (e.g., invalid refresh token), end session immediately
      if (!refreshed) {
        await handleSessionExpired(true)
        throw new Response(null, {
          status: 401,
          statusText: "Unauthorized",
        })
      }

      response = await send(true)
    }

    if (!response.ok) {
      // If still unauthorized and our token is expired or missing, end the session
      const tokens = secureStorage.getTokens()
      const accessExpired = isTokenExpired(tokens.access)
      const refreshExpired = isTokenExpired(tokens.refresh)
      const tokensFullyExpired = accessExpired && refreshExpired

      if (response.status === 401 && tokensFullyExpired) {
        await handleSessionExpired()
      } else if (
        response.status === 403 &&
        (attemptedRefresh || tokensFullyExpired)
      ) {
        await handleSessionExpired(true)
      }

      throw response
    }

    if (response.status === 204) {
      return null as T
    }

    const text = await response.text()
    if (!text) return null as T

    const result = JSON.parse(text) as T
    return result
  })()

  return requestPromise
}

export const api = {
  get: <T = unknown>(url: string, options?: ICustomRequestInit) =>
    apiWrapper(() => apiClient<T>(url, options)),

  post: <T = unknown>(url: string, options?: ICustomRequestInit) =>
    apiWrapper(() => apiClient<T>(url, { ...options, method: "POST" })),

  put: <T = unknown>(url: string, options?: ICustomRequestInit) =>
    apiWrapper(() => apiClient<T>(url, { ...options, method: "PUT" })),

  delete: <T = unknown>(url: string, options?: ICustomRequestInit) =>
    apiWrapper(() => apiClient<T>(url, { ...options, method: "DELETE" })),

  patch: <T = unknown>(url: string, options?: ICustomRequestInit) =>
    apiWrapper(() => apiClient<T>(url, { ...options, method: "PATCH" })),
}
